/**
 * The tabs of an object that are lists, not rows: its Columns, its Indexes,
 * its Triggers — and the Definition of a view or a function.
 *
 * One card of rows each, a name over its detail and a fact on the right. The
 * action that adds to the list lives in the bar above it; a row's own action
 * (Drop) is quiet text at its end, because a list of outlined buttons made
 * every row as tall as its button.
 *
 * Nothing here runs by itself: Drop opens the SQL dialog with the statement
 * written out, since a DROP is one keystroke from irreversible and should be
 * read by the person running it.
 */
import { AppDatabaseColumn, AppDatabaseIndex, AppDatabaseTrigger } from '@back4app2/sdk';
import LoadingSpinner from '../../components/LoadingSpinner';
import { SurfaceCard } from '../../components/ds';
import { formatBytes, qualifyName, quoteIdent, quotedTarget, shortTypeName } from './cells';
import { SqlDialogRequest } from './SqlDialog';

const DropAction = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="b4-focus flex-none h-7 px-2 -mr-1 rounded-control font-inter text-[length:calc(13px*var(--b4-type,1))] text-state-err/80 hover:text-state-err hover:bg-state-err/10 transition-colors duration-[120ms]"
  >
    Drop
  </button>
);

const ListRow = ({ name, detail, side, title, action }: {
  name: string,
  detail: React.ReactNode,
  side?: React.ReactNode,
  title?: string,
  action?: React.ReactNode,
}) => (
  <div
    title={title}
    className="flex flex-wrap items-center justify-between gap-x-3.5 gap-y-1.5 px-4 py-3.5 border-b border-navy-700 last:border-b-0"
  >
    <span className="flex flex-col gap-1 min-w-[180px] flex-1">
      <span className="font-courrier text-[length:calc(13.5px*var(--b4-type,1))] text-white break-all">{name}</span>
      <span className="font-courrier text-ds-115 text-navy-200">{detail}</span>
    </span>
    {side ? <span className="font-courrier text-ds-115 text-navy-300 whitespace-nowrap">{side}</span> : null}
    {action}
  </div>
);

const Empty = ({ title, children }: { title: string, children: React.ReactNode }) => (
  <div className="flex flex-col gap-1.5 px-[18px] py-[22px]">
    <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">{title}</span>
    <span className="text-ds-135 text-navy-200">{children}</span>
  </div>
);

const Card = ({ isLoading, error, children }: {
  isLoading?: boolean,
  error?: string,
  children: React.ReactNode,
}) => (
  <SurfaceCard tone="surface" className="overflow-hidden">
    {isLoading ? (
      <div className="flex justify-center py-10"><LoadingSpinner /></div>
    ) : error ? (
      <Empty title="We couldn't read this">{error}</Empty>
    ) : children}
  </SurfaceCard>
);

const columnDetail = (column: AppDatabaseColumn) => [
  shortTypeName(column.dataType),
  column.isPrimaryKey ? 'primary key' : undefined,
  !column.isPrimaryKey && !column.isNullable ? 'not null' : undefined,
  column.defaultValue ? `default ${column.defaultValue}` : undefined,
].filter(Boolean).join(' · ');

export const ColumnsList = ({ table, columns, hidden, isTable, isLoading, onAsk }: {
  table: { schema: string, name: string },
  columns: AppDatabaseColumn[],
  /** Columns hidden from the Data grid — said on the row, not acted on. */
  hidden: string[],
  isTable: boolean,
  isLoading: boolean,
  onAsk: (request: SqlDialogRequest) => void,
}) => (
  <Card isLoading={isLoading && columns.length === 0}>
    {columns.length === 0 ? (
      <Empty title="No columns yet">Add a column to start writing rows into {table.name}.</Empty>
    ) : columns.map(column => (
      <ListRow
        key={column.name}
        name={column.name}
        detail={columnDetail(column)}
        side={hidden.includes(column.name) ? 'hidden in Data' : undefined}
        // A primary key column is not dropped from here: it takes the table's
        // identity with it, and every row edit keys on it.
        action={isTable && !column.isPrimaryKey ? (
          <DropAction
            onClick={() => onAsk({
              title: 'Drop column',
              blurb: `Removes ${column.name} and every value stored in it. Views and indexes that use it make the drop fail.`,
              sql: `ALTER TABLE ${quotedTarget(table.schema, table.name)}\n  DROP COLUMN ${quoteIdent(column.name)};`,
              action: 'Drop',
            })}
          />
        ) : null}
      />
    ))}
  </Card>
);

/** `CREATE UNIQUE INDEX x ON t USING btree (email)` → `btree (email)`. */
const indexMethod = (definition: string) => {
  const match = /\sUSING\s+(\w+)\s+(.*)$/i.exec(definition);

  return match ? `${match[1]} ${match[2]}` : definition;
};

export const IndexesList = ({ table, indexes, isLoading, error, onAsk }: {
  table: { schema: string, name: string },
  indexes: AppDatabaseIndex[],
  isLoading: boolean,
  error?: string,
  onAsk: (request: SqlDialogRequest) => void,
}) => (
  <Card isLoading={isLoading && indexes.length === 0} error={error}>
    {indexes.length === 0 ? (
      <Empty title="No indexes yet">
        Every query against {table.name} reads the whole table.
      </Empty>
    ) : indexes.map(index => (
      <ListRow
        key={index.name}
        name={index.name}
        detail={indexMethod(index.definition)}
        title={index.definition}
        // Stated, not judged: a zero scan count can mean the statistics were
        // reset this morning as easily as an unused index.
        side={`${index.isPrimary ? 'primary key' : index.isUnique ? 'unique' : 'btree'} · ${formatBytes(index.sizeBytes)} · ${index.scans.toLocaleString()} scans`}
        action={index.isPrimary ? null : (
          <DropAction
            onClick={() => onAsk({
              title: 'Drop index',
              blurb: `Removes ${index.name}. Queries that relied on it fall back to a scan.`,
              sql: `DROP INDEX CONCURRENTLY IF EXISTS ${qualifyName(table.schema, index.name)};`,
              action: 'Drop',
            })}
          />
        )}
      />
    ))}
  </Card>
);

export const TriggersList = ({ table, triggers, isLoading, error, onAsk }: {
  table: { schema: string, name: string },
  triggers: AppDatabaseTrigger[],
  isLoading: boolean,
  error?: string,
  onAsk: (request: SqlDialogRequest) => void,
}) => (
  <Card isLoading={isLoading && triggers.length === 0} error={error}>
    {triggers.length === 0 ? (
      <Empty title="No triggers">Nothing runs automatically on writes to {table.name}.</Empty>
    ) : triggers.map(trigger => (
      <ListRow
        key={trigger.name}
        name={trigger.name}
        detail={`${trigger.timing} ${trigger.events.join(' OR ')} · EXECUTE FUNCTION ${trigger.functionName}()`}
        side={`${trigger.orientation} level${trigger.isEnabled ? '' : ' · disabled'}`}
        action={
          <DropAction
            onClick={() => onAsk({
              title: 'Drop trigger',
              blurb: `Removes ${trigger.name}. ${trigger.functionName}() stays — a trigger and its function are separate objects.`,
              sql: `DROP TRIGGER IF EXISTS ${trigger.name} ON ${qualifyName(table.schema, table.name)};`,
              action: 'Drop',
            })}
          />
        }
      />
    ))}
  </Card>
);

export const DefinitionView = ({ sql }: { sql: string }) => (
  <pre className="m-0 p-4 rounded-card border border-navy-500 bg-navy-950 overflow-auto b4-thin font-courrier text-ds-125 leading-[1.7] text-navy-100 whitespace-pre-wrap">
    {sql}
  </pre>
);
